import { ChatMessage, ChatCommand, ChatQueryResult } from './types';
import { CommandParser } from './CommandParser';
import { RephrasingService } from '../rephrasing/RephrasingService';
import { log, logError } from '../../utils/logging';
import { AppError } from '../../utils/errorHandling';
import apiService from '../../services/api';
import { getReviewPartyContextInstruction } from '../../utils/reviewPartyContext';
import i18n from 'i18next';

/**
 * Copy the Editor shows as the explanation for `rephrase`, which calls a
 * dedicated endpoint and so has no model-written explanation of its own.
 * Hardcoding it in English is why the panel's language used to depend on which
 * route the request happened to take.
 */
function editorCopy(key: 'rephraseDone'): string {
  return i18n.t(`editor.${key}`, { ns: 'taskpane' });
}

export class ChatService {
  private rephrasingService: RephrasingService;
  private messageHistory: ChatMessage[] = [];

  constructor(rephrasingService: RephrasingService) {
    this.rephrasingService = rephrasingService;
  }

  /**
   * Process a user query and return a response
   */
  async processQuery(query: string, selectedText: string): Promise<ChatQueryResult> {
    try {
      log(`Processing query: "${query}" with selected text length: ${selectedText.length}`);

      // Parse the command
      const command = CommandParser.parse(query);
      log(`Command parsed as type: ${command.type}, intent: ${command.intent}`);

      // Handle different command types
      switch (command.type) {
        case 'rephrase':
          return await this.handleRephrase(selectedText);
        
        case 'custom':
        case 'unknown':
          return await this.handleCustomQuery(query, selectedText, command);
        
        default:
          throw new AppError('Unknown command type', 'UNKNOWN_COMMAND', 'Could not understand the command.');
      }
    } catch (error) {
      logError('Failed to process query', error);
      throw error;
    }
  }

  /**
   * Process a user query with streaming support
   * Yields text chunks as they arrive, then final result
   */
  async *processQueryStream(query: string, selectedText: string): AsyncGenerator<
    { type: string; content?: string; result?: ChatQueryResult; error?: string },
    void,
    unknown
  > {
    try {
      log(`Processing query with streaming: "${query}" with selected text length: ${selectedText.length}`);

      // Parse the command
      const command = CommandParser.parse(query);
      log(`Command parsed as type: ${command.type}, intent: ${command.intent}`);

      // Handle different command types
      switch (command.type) {
        case 'rephrase':
          // Rephrase could support streaming in the future
          const rephraseResult = await this.handleRephrase(selectedText);
          yield {
            type: 'complete',
            result: rephraseResult
          };
          break;
        
        case 'custom':
        case 'unknown':
          // Custom queries could support streaming in the future
          const customResult = await this.handleCustomQuery(query, selectedText, command);
          yield {
            type: 'complete',
            result: customResult
          };
          break;
        
        default:
          throw new AppError('Unknown command type', 'UNKNOWN_COMMAND', 'Could not understand the command.');
      }
    } catch (error) {
      logError('Failed to process query with streaming', error);
      yield {
        type: 'error',
        error: error instanceof Error ? error.message : 'Failed to process query'
      };
    }
  }

  /**
   * Handle rephrase command using the existing rephrasing service
   */
  private async handleRephrase(text: string): Promise<ChatQueryResult> {
    log('Handling rephrase command');
    
    if (!text || text.trim().length === 0) {
      throw new AppError('No text selected', 'NO_TEXT', 'Please select some text to rephrase.');
    }

    // Use the rephrasing service's complete rephrase method
    const result = await this.rephrasingService.rephraseComplete();
    
    return {
      response: editorCopy('rephraseDone'),
      processedText: result.rephrasedText,
      confidence: result.confidence,
      paragraphXml: result.paragraphXml  // Pass XML from backend if available
    };
  }

  /**
   * Handle custom or unknown queries by sending to backend API
   */
  private async handleCustomQuery(query: string, text: string, command: ChatCommand): Promise<ChatQueryResult> {
    log(`Handling custom/unknown query: ${command.intent}`);
    
    if (!text || text.trim().length === 0) {
      throw new AppError('No text selected', 'NO_TEXT', 'Please select some text in the document.');
    }

    try {
      // Call the backend chat API (inject Review party identification when available)
      const response = await apiService.chatQuery(
        text,
        query,
        undefined,
        getReviewPartyContextInstruction()
      );
      
      if (!response.success || !response.data) {
        throw new AppError('Invalid response', 'INVALID_RESPONSE', 'The AI service returned an invalid response.');
      }

      return {
        response: response.data.response || 'Here is the processed text:',
        processedText: response.data.processed_text,
        confidence: response.data.confidence
      };
    } catch (error: any) {
      logError('Custom query failed', error);
      
      // Check if it's a 404 (endpoint not implemented)
      if (error?.response?.status === 404 || error?.code === 'ERR_BAD_REQUEST') {
        throw new AppError(
          'Chat endpoint not available',
          'ENDPOINT_NOT_FOUND',
          'The chat endpoint is not yet implemented on the backend. Implement the /add-ins/chat endpoint.'
        );
      }
      
      throw new AppError(
        'Query failed',
        'QUERY_FAILED',
        error instanceof Error ? error.message : 'Failed to process your query. Please try again.'
      );
    }
  }

  /**
   * Add a message to the history
   */
  addMessage(message: ChatMessage): void {
    this.messageHistory.push(message);
  }

  /**
   * Get message history
   */
  getHistory(): ChatMessage[] {
    return [...this.messageHistory];
  }

  /**
   * Clear message history
   */
  clearHistory(): void {
    this.messageHistory = [];
  }

  /**
   * Get the rephrasing service instance
   */
  getRephrasingService(): RephrasingService {
    return this.rephrasingService;
  }
}

